import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { DefaultButton, Icon, Spinner } from "@fluentui/react";
import { logger } from "cayo.ui";
import styled from "styled-components";
import downloadUtils from "@cayo/shared/utils/download";
import { createDownloadADConnector } from "../api";
import { wizardMessages } from "../messages";
const log = logger.getLogger("DownloadAdConnectorConfig");

type DownloadAdConnectorConfigProps = {
  onConfigDownload: () => void;
  checkingAdStatus: boolean;
  agentIsOnline: boolean | undefined;
};

const DownloadAdConnectorConfig = ({
  onConfigDownload,
  agentIsOnline,
  checkingAdStatus,
}: DownloadAdConnectorConfigProps) => {
  const intl = useIntl();

  const handleDownload = async () => {
    try {
      const { data, headers } = await createDownloadADConnector();

      downloadUtils.downloadStream(data as Blob, headers);
      onConfigDownload();
    } catch (error) {
      log.debug(error);
    }
  };

  return (
    <Wrapper>
      <DownloadRow>
        <DefaultButton
          text={intl.formatMessage(wizardMessages.download)}
          iconProps={{ iconName: "Download" }}
          onClick={handleDownload}
          disabled={checkingAdStatus}
        />
        <DownloadState
          className={agentIsOnline === true ? "online" : agentIsOnline === false ? "invalid" : ""}
        >
          {checkingAdStatus && <Spinner label={""} labelPosition="right" />}
          {!checkingAdStatus && agentIsOnline === false && <Icon iconName="ErrorBadge" />}
          {!checkingAdStatus && agentIsOnline === true && <Icon iconName="Completed" />}

          {checkingAdStatus && (
            <InprogressText>
              {intl.formatMessage(wizardMessages.adConnectorConfigIsInProgress)}
            </InprogressText>
          )}
        </DownloadState>
      </DownloadRow>

      <Instructions>
        <InstructionsTitle>
          {intl.formatMessage(wizardMessages.adConnectorConfigInstructionsTitle, {
            link: (
              <a
                href={"https://support.cayosoft.com/hc/en-us/articles/43507970743821"}
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage
                  id={wizardMessages.LearnMore.id}
                  defaultMessage={wizardMessages.LearnMore.defaultMessage}
                />
              </a>
            ),
          })}
        </InstructionsTitle>

        <InstructionsList>
          <li>
            <FormattedMessage {...wizardMessages.adConnectorConfigStep1} />
          </li>
          <li>
            <FormattedMessage {...wizardMessages.adConnectorConfigStep2} />
          </li>
          <li>
            <FormattedMessage {...wizardMessages.adConnectorConfigStep3} />
          </li>
          <li>
            <FormattedMessage {...wizardMessages.adConnectorConfigStep4} />
          </li>
          <li>
            <FormattedMessage {...wizardMessages.adConnectorConfigStep5} />
          </li>
        </InstructionsList>
      </Instructions>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InprogressText = styled.span`
  font-size: 12px;
`;

const DownloadRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  gap: 10px;

  button {
    width: 120px;
  }
`;

const Instructions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InstructionsTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const InstructionsList = styled.ol`
  margin: 0;
  padding-left: 20px;
  font-size: 13px;

  li {
    margin-bottom: 6px;
    line-height: 1.4;
  }
`;

const DownloadState = styled.span`
  font-weight: 500;
  display: flex; 
  gap 10px;
  align-items: center;

  .consent {
    color: rgb(50, 49, 48) !important;
  }

  &.online {
    color: ${(props) => props.theme.cayoTheme.brandColors.success};
  }

  &.invalid {
    color: ${(props) => props.theme.cayoTheme.brandColors.error};
  }
`;

export default DownloadAdConnectorConfig;
